import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  Row,
  Col,
  Statistic,
  Typography,
  Space,
  Button,
  Grid,
  Avatar,
  Badge,
  Alert,
  Descriptions,
  Spin,
  Divider,
  Tooltip,
  theme,
  Modal,
  List,
  Radio,
  Select
} from 'antd';
import {
  UserOutlined,
  HomeOutlined,
  CalendarOutlined,
  TeamOutlined,
  AppstoreOutlined,
  BellOutlined,
  LogoutOutlined,
  BugOutlined,
  DashboardOutlined,
  SettingOutlined,
  InfoCircleOutlined,
  CheckOutlined
} from '@ant-design/icons';
import axios from 'axios';

import {BASE_URL} from '../utils/constants'



const { Title, Text, Paragraph } = Typography;
const { useBreakpoint, useBreakpoint: screens } = Grid;
const { Option } = Select;

const Dashboard = () => {
  const [userData, setUserData] = useState({});
  const [loading, setLoading] = useState(true);
  const [debugInfo, setDebugInfo] = useState({});
  const navigate = useNavigate();
  const breakpoints = useBreakpoint();
  const { token } = theme.useToken();
  const [hotel, setHotel] = useState('');
  const [hotelCodigo, setHotelCodigo] = useState(null);
  
  // Estados para el modal de selección de hotel
  const [hoteles, setHoteles] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedHotel, setSelectedHotel] = useState(null);
  const [loadingHoteles, setLoadingHoteles] = useState(false);

  // Determinar el tamaño de la pantalla
  const isMobile = !breakpoints.md;
  const isTablet = breakpoints.md && !breakpoints.lg;
  const isDesktop = breakpoints.lg;

  useEffect(() => {
    console.log('📊 Dashboard montado');
    fetchUserProfile();
    checkAuthStatus();
    fetchHotelInfo();
    
    // Cargar lista de hoteles al montar el componente
    fetchHoteles();
  }, []);

  const fetchHoteles = async () => {
    try {
      setLoadingHoteles(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${BASE_URL}/api/hoteles/listaHoteles`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setHoteles(response.data.hoteles);
        
        // Si no hay hotel seleccionado, seleccionar el primero por defecto
        if (response.data.hoteles.length > 0 && !hotelCodigo) {
          const primerHotel = response.data.hoteles[0];
          setSelectedHotel(primerHotel.codigo);
          // Aquí podrías también guardar la selección en localStorage si lo deseas
        }
      }
    } catch (error) {
      console.error('Error obteniendo lista de hoteles:', error);
    } finally {
      setLoadingHoteles(false);
    }
  };

  const fetchHotelInfo = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${BASE_URL}/api/gobernanta/hotel-info`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setHotel(response.data.hotel.nombre);
        setHotelCodigo(response.data.hotel.codigo);
      }
    } catch (error) {
      console.error('Error obteniendo información del hotel:', error);
    }
  };

  const fetchUserProfile = async () => {
    try {
      // console.log('👤 Obteniendo perfil del usuario...');
      const token = localStorage.getItem('token');
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      
      // console.log('🔍 Token disponible:', !!token);
      // console.log('👤 User en localStorage:', user);
      
      const response = await axios.get(`${BASE_URL}/api/usuarios/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        // console.log('✅ Perfil obtenido:', response.data.user);
        setUserData(response.data.user);
      }
    } catch (error) {
      console.error('❌ Error obteniendo perfil:', error);
      if (error.response?.status === 401) {
        console.log('⚠️  Token inválido - redirigiendo a login');
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const checkAuthStatus = () => {
    const token = localStorage.getItem('token');
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    
    setDebugInfo({
      tokenPresent: !!token,
      tokenLength: token?.length || 0,
      userPresent: !!user.usuario,
      userData: user,
      timestamp: new Date().toISOString(),
      screenSize: {
        isMobile,
        isTablet,
        isDesktop,
        breakpoints
      }
    });
    
    // console.log('🔍 Estado de autenticación:', {
    //   tokenPresent: !!token,
    //   userPresent: !!user.usuario,
    //   user: user,
    //   screenSize: { isMobile, isTablet, isDesktop }
    // });
  };

  const updateUltimoHotel = async (codigoHotel) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${BASE_URL}/api/hoteles/ultimoHotel`,
        { codigoHotel },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // if (response.data.success) {
      //   message.success(response.data.message);
      // }
    } catch (error) {
      console.error('Error cambiando estado de incidencia:', error);
      message.error('Error al cambiar estado de la incidencia');
    }
  };  

  const handleLogout = () => {
    console.log('👋 Cerrando sesión...');
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  const handleDebug = () => {
    console.log('🐛 Información de debug:', debugInfo);
    alert(JSON.stringify(debugInfo, null, 2));
  };

  // Función para abrir el modal de selección de hotel
  const handleHotelClick = () => {
    setModalVisible(true);
  };

  // Función para seleccionar un hotel
  const handleSelectHotel = async (codigoHotel) => {
    try {
      const token = localStorage.getItem('token');
      // Aquí debes implementar la API para cambiar el hotel seleccionado
      // Por ahora, solo actualizamos el estado local
      const hotelSeleccionado = hoteles.find(h => h.codigo === codigoHotel);
      if (hotelSeleccionado) {
        setHotel(hotelSeleccionado.nombre);
        setHotelCodigo(codigoHotel);
        setSelectedHotel(codigoHotel);

        updateUltimoHotel(codigoHotel);
        
        // Guardar en localStorage para persistencia
        localStorage.setItem('hotelSeleccionado', JSON.stringify({
          codigo: codigoHotel,
          nombre: hotelSeleccionado.nombre,
          terminal: hotelSeleccionado.terminal
        }));
        
        // Cerrar el modal
        setModalVisible(false);
        
        // Mostrar mensaje de éxito
        Modal.success({
          title: 'Hotel cambiado',
          content: `Ahora estás en: ${hotelSeleccionado.nombre}`,
          okText: 'Aceptar'
        });
      }
    } catch (error) {
      console.error('Error al cambiar de hotel:', error);
      Modal.error({
        title: 'Error',
        content: 'No se pudo cambiar el hotel seleccionado',
        okText: 'Aceptar'
      });
    }
  };

  // Función para renderizar cada hotel en la lista
  const renderHotelItem = (hotel) => (
    <List.Item
      style={{
        padding: '12px 16px',
        borderRadius: '8px',
        cursor: 'pointer',
        border: selectedHotel === hotel.codigo ? `2px solid ${token.colorPrimary}` : '1px solid #f0f0f0',
        backgroundColor: selectedHotel === hotel.codigo ? `${token.colorPrimary}08` : 'white',
        transition: 'all 0.3s',
        marginBottom: '8px'
      }}
      onClick={() => handleSelectHotel(hotel.codigo)}
    >
      <Space direction="vertical" size="small" style={{ width: '100%' }}>
        <Row justify="space-between" align="middle">
          <Col>
            <Space>
              <Avatar 
                size="small"
                icon={<HomeOutlined />}
                style={{ 
                  backgroundColor: selectedHotel === hotel.codigo ? token.colorPrimary : '#f0f0f0',
                  color: selectedHotel === hotel.codigo ? 'white' : '#666'
                }}
              />
              <Text strong style={{ fontSize: '16px' }}>{hotel.nombre}</Text>
            </Space>
          </Col>
          {selectedHotel === hotel.codigo && (
            <Col>
              <CheckOutlined style={{ color: token.colorPrimary }} />
            </Col>
          )}
        </Row>
        <Row>
          <Col span={24}>
            <Space direction="vertical" size={2}>
              <Text type="secondary" style={{ fontSize: '12px' }}>
                Código: <Text strong>{hotel.codigo}</Text>
              </Text>
            </Space>
          </Col>
        </Row>
      </Space>
    </List.Item>
  );

  const menuCards = [
    {
      title: 'Gobernanta',
      icon: <TeamOutlined style={{ fontSize: isMobile ? 20 : 24 }} />,
      description: 'Control de habitaciones y limpieza',
      status: 'Próximamente',
      color: '#52c41a',
      key: 'gobernanta'
    }
  ];

  const quickActions = [
    {
      key: 'gobernanta',
      label: 'Gobernanta',
      icon: <TeamOutlined />,
      onClick: () => console.log('Ir a Gobernanta'),
      color: '#52c41a'
    },
    {
      key: 'reportes',
      label: 'Reportes',
      icon: <CalendarOutlined />,
      onClick: () => console.log('Ver reportes'),
      color: '#1890ff'
    },
    {
      key: 'configuracion',
      label: 'Configuración',
      icon: <SettingOutlined />,
      onClick: () => console.log('Ir a Configuración'),
      color: '#722ed1'
    }
  ];

  const stats = [
    {
      title: 'Hotel',
      value: hotel || 'Seleccionar Hotel',
      icon: <HomeOutlined />,
      suffix: '',
      tooltip: 'Click para cambiar de hotel',
      onClick: handleHotelClick
    },
    // ... otros stats
  ];

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '50vh',
        flexDirection: 'column',
        padding: isMobile ? '20px' : '40px'
      }}>
        <Spin size="large" tip="Cargando dashboard..." />
        <Text type="secondary" style={{ marginTop: 20 }}>
          Preparando tu información...
        </Text>
      </div>
    );
  }

  return (
    <div style={{ 
      padding: isMobile ? '12px' : '24px',
      maxWidth: '100%',
      overflowX: 'hidden'
    }}>
      <Space 
        direction="vertical" 
        size={isMobile ? "middle" : "large"} 
        style={{ width: '100%' }}
      >
        

        {/* Header */}
        <Card 
          bordered={false}
          style={{ 
            borderRadius: '8px',
            background: `linear-gradient(135deg, ${token.colorPrimary} 0%, ${token.colorPrimaryActive} 100%)`,
            color: 'white',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
          }}
          bodyStyle={{ padding: isMobile ? '16px' : '24px' }}
        >
          <Row 
            justify="space-between" 
            align="middle"
            gutter={[16, 16]}
            wrap={!isMobile}
          >
            <Col flex={isMobile ? "100%" : "auto"}>
              <Space direction="vertical" size={isMobile ? "small" : "middle"}>
                <Title 
                  level={isMobile ? 3 : 2} 
                  style={{ 
                    margin: 0, 
                    color: 'white',
                    fontWeight: 600
                  }}
                >
                  Dashboard
                </Title>
                <Paragraph 
                  style={{ 
                    margin: 0, 
                    color: 'rgba(255, 255, 255, 0.85)',
                    fontSize: isMobile ? '14px' : '16px'
                  }}
                >
                  Bienvenido, {userData.UsuarioCorto || userData.usuario || 'Usuario'}
                </Paragraph>
              </Space>
            </Col>
            
            <Col flex={isMobile ? "100%" : "auto"}>
              <Row 
                gutter={isMobile ? [8, 8] : [12, 0]} 
                justify={isMobile ? "start" : "end"}
                wrap={isMobile}
              >
                <Col>
                  <Button 
                    type="default"
                    icon={<BellOutlined />}
                    onClick={() => console.log('Notificaciones')}
                    style={{ 
                      background: 'rgba(255, 255, 255, 0.15)',
                      borderColor: 'rgba(255, 255, 255, 0.3)',
                      color: 'white'
                    }}
                    size={isMobile ? "middle" : "large"}
                  >
                    {isDesktop && 'Notificaciones'}
                  </Button>
                </Col>
                <Col>
                  <Button 
                    danger
                    icon={<LogoutOutlined />}
                    onClick={handleLogout}
                    size={isMobile ? "middle" : "large"}
                  >
                    {isDesktop && 'Cerrar Sesión'}
                  </Button>
                </Col>
              </Row>
            </Col>
          </Row>
        </Card>

        {/* Stats Grid - Responsive */}
        <Row gutter={[16, 16]}>
          {stats.map((stat, index) => (
            <Col 
              key={index}
              xs={24} 
              sm={12} 
              md={12} 
              lg={6}
            >
              <Tooltip title={stat.tooltip}>
                <Card
                  hoverable
                  onClick={stat.onClick}
                  style={{ 
                    borderRadius: '8px',
                    height: '100%',
                    transition: 'all 0.3s',
                    cursor: 'pointer'
                  }}
                  bodyStyle={{ 
                    padding: isMobile ? '16px' : '20px'
                  }}
                >
                  <Space 
                    direction="vertical" 
                    size="small" 
                    style={{ width: '100%' }}
                  >
                    <Row align="middle" gutter={8} wrap={false}>
                      <Col>
                        <Avatar 
                          size={isMobile ? 32 : 40}
                          icon={stat.icon}
                          style={{ 
                            backgroundColor: token.colorPrimaryBg,
                            color: token.colorPrimary
                          }}
                        />
                      </Col>
                      <Col flex="auto">
                        <Text 
                          type="secondary" 
                          style={{ 
                            fontSize: isMobile ? '12px' : '14px',
                            display: 'block'
                          }}
                        >
                          {stat.title}
                        </Text>
                      </Col>
                    </Row>
                    <Space 
                      align="baseline" 
                      style={{ width: '100%' }}
                    >
                      <Title 
                        level={isMobile ? 4 : 3} 
                        style={{ 
                          margin: 0,
                          fontSize: isMobile ? '20px' : '24px',
                          color: hotel ? 'inherit' : token.colorPrimary
                        }}
                      >
                        {stat.value}
                      </Title>
                      {stat.suffix && (
                        <Text type="secondary">{stat.suffix}</Text>
                      )}
                    </Space>
                  </Space>
                </Card>
              </Tooltip>
            </Col>
          ))}
        </Row>

        {/* Modal de selección de hotel */}
        <Modal
          title={
            <Space>
              <HomeOutlined />
              <span>Seleccionar Hotel</span>
            </Space>
          }
          open={modalVisible}
          onCancel={() => setModalVisible(false)}
          footer={[
            <Button key="cancel" onClick={() => setModalVisible(false)}>
              Cancelar
            </Button>,
            selectedHotel && (
              <Button 
                key="select" 
                type="primary" 
                onClick={() => handleSelectHotel(selectedHotel)}
                loading={loadingHoteles}
              >
                Seleccionar
              </Button>
            )
          ]}
          width={isMobile ? '90%' : 500}
          centered
        >
          {loadingHoteles ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <Spin size="large" tip="Cargando hoteles..." />
            </div>
          ) : hoteles.length === 0 ? (
            <Alert
              message="No hay hoteles disponibles"
              description="No se encontraron hoteles para mostrar."
              type="warning"
              showIcon
            />
          ) : (
            <Space direction="vertical" style={{ width: '100%' }} size="middle">
              <Text type="secondary">
                Selecciona uno de los hoteles disponibles:
              </Text>
              
              <List
                dataSource={hoteles}
                renderItem={renderHotelItem}
                locale={{ emptyText: 'No hay hoteles disponibles' }}
              />
              
              <Alert
                message="Información"
                description="Al cambiar de hotel, algunas configuraciones podrían verse afectadas."
                type="info"
                showIcon
                style={{ marginTop: '16px' }}
              />
            </Space>
          )}
        </Modal>

        {/* Footer/Información adicional */}
        {isDesktop && (
          <Card 
            bordered={false}
            style={{ 
              borderRadius: '8px',
              background: token.colorFillAlter,
              textAlign: 'center'
            }}
            bodyStyle={{ padding: '16px' }}
          >
            <Space direction="vertical" size="small">
              <Text type="secondary">
                app Anphitrion v1.0.0
              </Text>
              <Text type="secondary" style={{ fontSize: '12px' }}>
                © {new Date().getFullYear()} - Todos los derechos reservados
              </Text>
            </Space>
          </Card>
        )}
      </Space>
    </div>
  );
};

export default Dashboard;